import * as React from "react";
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Field } from "@/components/ui/field";
import { Input, Textarea } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/misc";
import type { FlowNode, Question } from "@/lib/intake-flow";

/**
 * Setting up one step: which question it asks, or what the outcome says (ACL-338).
 *
 * The question picker is the contact form's own fields: this never creates or edits one, so
 * attaching a question to a flow can never change the contact record's shape. A question whose
 * answer cannot be branched on (free text, a date) says so, because a rule on it could only ask
 * whether it was answered at all.
 */
export function StepDrawer({
  open,
  onOpenChange,
  node,
  questions,
  asked,
  canBranchHere,
  onSave,
  onBranchOnAnswer,
  onNewQuestion,
  dispositions = [],
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  node: FlowNode | null;
  questions: Question[];
  /** Field keys already asked in this flow, which are dimmed in the picker. */
  asked: Set<string>;
  /** Whether this step still has one way out, so branching on its answer is offered. */
  canBranchHere: boolean;
  onSave: (patch: Partial<FlowNode>) => void;
  /** Make one path per answer, which is the usual next thing after picking a question. */
  onBranchOnAnswer: (question: Question) => void;
  /** Add a question to the contact form without leaving the builder. */
  onNewQuestion: () => void;
  /** The outcomes this tenant offers, which a reject may propose and nothing else. */
  dispositions?: { slug: string; label: string; kind: string }[];
}) {
  const [title, setTitle] = React.useState("");
  const [fieldKey, setFieldKey] = React.useState<string>("");
  const [mustAnswer, setMustAnswer] = React.useState(true);
  const [help, setHelp] = React.useState("");
  const [reason, setReason] = React.useState("");
  const [visibleTo, setVisibleTo] = React.useState("supervisor");
  const [disposition, setDisposition] = React.useState("");

  React.useEffect(() => {
    if (!open || !node) return;
    setTitle(node.title);
    setFieldKey(node.field_key ?? "");
    setMustAnswer(node.config?.must_answer !== false);
    setHelp(typeof node.config?.help === "string" ? node.config.help : "");
    setReason(typeof node.config?.reason === "string" ? node.config.reason : "");
    setVisibleTo(typeof node.config?.visible_to === "string" ? node.config.visible_to : "supervisor");
    setDisposition(typeof node.config?.disposition === "string" ? node.config.disposition : "");
  }, [open, node]);

  if (!node) return null;
  const isQuestion = node.kind === "question";
  const isFlag = node.kind === "outcome" && node.outcome === "flag";
  const isReject = node.kind === "outcome" && node.outcome === "reject";
  const picked = questions.find((q) => q.key === fieldKey);

  function save() {
    const config: Record<string, unknown> = { ...node!.config };
    if (isQuestion) {
      config.must_answer = mustAnswer;
      if (help.trim()) config.help = help.trim();
      else delete config.help;
    }
    if (isFlag) {
      config.reason = reason.trim();
      config.visible_to = visibleTo;
    }
    if (isReject) {
      config.disposition = disposition.trim();
      config.reason = reason.trim();
    }
    onSave({
      title: title.trim() || node!.title,
      field_key: isQuestion ? fieldKey || null : null,
      config,
    });
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent variant="drawer">
        <DialogHeader>
          <DialogTitle>{isQuestion ? "The question" : "The outcome"}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <Field label="What this step is called" htmlFor="step-title">
            <Input id="step-title" value={title} onChange={(e) => setTitle(e.target.value)} />
          </Field>

          {isQuestion ? (
            <>
              <Field
                label="Which question to ask"
                htmlFor="step-field"
                hint="The contact form's own questions. Add a new one on the Custom fields page."
              >
                <Select value={fieldKey} onValueChange={setFieldKey}>
                  <SelectTrigger id="step-field">
                    <SelectValue placeholder="Pick a question" />
                  </SelectTrigger>
                  <SelectContent>
                    {questions.map((q) => (
                      <SelectItem
                        key={q.key}
                        value={q.key}
                        disabled={asked.has(q.key) && q.key !== node.field_key}
                      >
                        {q.title}
                        {asked.has(q.key) && q.key !== node.field_key ? " — asked already" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Button variant="outline" size="sm" onClick={onNewQuestion}>
                Add a new question
              </Button>
              {picked && !picked.can_branch ? (
                <p className="text-xs text-text-muted">
                  Answers to this question are free text, so a branch can only ask whether it was
                  answered at all.
                </p>
              ) : null}
              {picked?.can_branch && canBranchHere ? (
                <div className="rounded-lg border border-border bg-surface-sunken/60 p-3">
                  <p className="text-sm font-medium text-text">Ask different questions per answer?</p>
                  <p className="mt-1 text-xs text-text-muted">
                    Makes one path for each answer, and a last one for everyone else. Each path then
                    asks its own questions.
                  </p>
                  <ul className="mt-2 flex flex-wrap gap-1">
                    {(picked.type === "boolean"
                      ? [
                          { key: "true", label: "Yes" },
                          { key: "false", label: "No" },
                        ]
                      : picked.options
                    ).map((option) => (
                      <li
                        key={option.key}
                        className="rounded-full border border-border bg-surface px-2 py-0.5 text-[11px] text-text-muted"
                      >
                        {option.label}
                      </li>
                    ))}
                  </ul>
                  <Button
                    className="mt-3"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      // Save the question first, so the branches are made against the right one.
                      save();
                      onBranchOnAnswer(picked);
                    }}
                  >
                    Make a path per answer
                  </Button>
                </div>
              ) : null}
              <Field label="The rep has to answer it" htmlFor="step-must">
                <Switch id="step-must" checked={mustAnswer} onCheckedChange={setMustAnswer} />
              </Field>
              <Field label="Help for the rep" htmlFor="step-help">
                <Textarea id="step-help" rows={2} value={help} onChange={(e) => setHelp(e.target.value)} />
              </Field>
            </>
          ) : null}

          {isFlag ? (
            <>
              <Field
                label="Why it is flagged"
                htmlFor="step-reason"
                hint="A supervisor reads this. Never put an answer in it."
              >
                <Input id="step-reason" maxLength={200} value={reason} onChange={(e) => setReason(e.target.value)} />
              </Field>
              <Field label="Who may read the reason" htmlFor="step-visible">
                <Select value={visibleTo} onValueChange={setVisibleTo}>
                  <SelectTrigger id="step-visible">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="supervisor">Supervisors and above</SelectItem>
                    <SelectItem value="admin">Admins only</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </>
          ) : null}

          {isReject ? (
            <>
              <Field
                label="The outcome to propose"
                htmlFor="step-disposition"
                hint={
                  dispositions.length
                    ? "The rep records it; the intake never moves a lead itself."
                    : "This tenant offers no outcomes yet. Add one on Settings › Dispositions, or this step cannot be published."
                }
                error={
                  // Said here rather than only at publish: the slug used to be free text, so a
                  // name that was never an outcome looked fine until the whole flow was refused.
                  disposition && !dispositions.some((one) => one.slug === disposition)
                    ? `"${disposition}" is not one of this tenant's outcomes`
                    : undefined
                }
              >
                <Select value={disposition} onValueChange={setDisposition}>
                  <SelectTrigger id="step-disposition" aria-label="The outcome to propose">
                    <SelectValue placeholder="Pick an outcome" />
                  </SelectTrigger>
                  <SelectContent>
                    {dispositions.map((one) => (
                      <SelectItem key={one.slug} value={one.slug}>
                        {one.label}
                        {/* A reject that proposes a retry outcome is legal but rarely meant,
                            so the kind is shown rather than left to be remembered. */}
                        {one.kind === "terminal" ? "" : ` · ${one.kind}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Why" htmlFor="step-reject-reason">
                <Input id="step-reject-reason" maxLength={200} value={reason} onChange={(e) => setReason(e.target.value)} />
              </Field>
            </>
          ) : null}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={save}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
